/**
 * Carbon form field frame: a 12px label above the control (8px gap), then one
 * line under it, either helper text or, for blocking validation, the error in
 * Coral ink. The control itself is the child; it must carry `id` and point
 * `aria-describedby` at `fieldIds()` (the field primitives do this already).
 * Use it directly only for a control the kit doesn't cover.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { fieldIds } from './fieldStyles'

export interface FormFieldProps {
  /** The control's id; the label points at it. */
  id: string
  label: ReactNode
  helper?: ReactNode
  /** Blocking validation only. Replaces the helper line while set. */
  error?: ReactNode
  /** Keep the label for screen readers only (a field inside a table row, say). */
  hideLabel?: boolean
  disabled?: boolean
  className?: string
  children: ReactNode
}

export function FormField({ id, label, helper, error, hideLabel, disabled, className, children }: FormFieldProps) {
  const { helperId, errorId } = fieldIds(id, helper, error)
  return (
    <div className={clsx('flex min-w-0 flex-col', className)}>
      <label
        htmlFor={id}
        className={clsx(
          hideLabel ? 'sr-only' : 'mb-2 text-helper',
          disabled ? 'text-text-placeholder' : 'text-text-secondary',
        )}
      >
        {label}
      </label>
      {children}
      {error ? (
        <p id={errorId} className="mt-1 text-helper text-crit-ink">
          {error}
        </p>
      ) : helper ? (
        <p id={helperId} className={clsx('mt-1 text-helper', disabled ? 'text-text-placeholder' : 'text-text-helper')}>
          {helper}
        </p>
      ) : null}
    </div>
  )
}

export default FormField
